/**
 * Avatar utility functions.
 *
 * Provides initials extraction and consistent color generation for user avatars.
 * Used in the board sharing modal to display member avatars as colored initials circles.
 */

/**
 * Extracts initials from a display name.
 *
 * Takes the first letter of the first word and the first letter of the last word,
 * both uppercased. For single-word names, returns just the first letter.
 * For empty or whitespace-only strings, returns "?".
 */
export function getInitials(displayName: string): string {
  const trimmed = (displayName ?? '').trim();

  if (trimmed.length === 0) {
    return '?';
  }

  const words = trimmed.split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  const firstInitial = words[0][0].toUpperCase();
  const lastInitial = words[words.length - 1][0].toUpperCase();

  return firstInitial + lastInitial;
}

/**
 * Derives a consistent HSL background color from a userId.
 *
 * Computes a simple hash from the userId string (sum of character codes)
 * and maps it to a hue value. Saturation and lightness are fixed to produce
 * visually consistent, readable avatar backgrounds.
 */
export function getAvatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = hash + userId.charCodeAt(i);
  }

  const hue = hash % 360;

  return `hsl(${hue}, 65%, 45%)`;
}
